import React, { useRef, useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/appConstants";
import "./ProviderProfileComponent.scss";

export interface SendEmailDialogProviderInfo {
  agencyName?: string;
  mpi?: string | number;
  primaryContactFirstName?: string;
  primaryContactLastName?: string;
  primaryContactEmail?: string;
}

export interface SendEmailDialogComponentProps {
  visible: boolean;
  onHide: () => void;
  providerInfo: SendEmailDialogProviderInfo;
  subjectOptions?: { label: string; value: string }[];
  onSend?: (payload: {
    subject: string;
    message: string;
    attachments: File[];
    toEmail?: string;
  }) => void;
}

const DEFAULT_SUBJECT_OPTIONS = [
  { label: "Request for updated documents", value: "Request for updated documents" },
  { label: "Request for clarification", value: "Request for clarification" },
  { label: "Request for additional information", value: "Request for additional information" },
  { label: "Other", value: "Other" },
];

const SendEmailDialogComponent: React.FC<SendEmailDialogComponentProps> = ({
  visible,
  onHide,
  providerInfo,
  subjectOptions = DEFAULT_SUBJECT_OPTIONS,
  onSend,
}) => {
  const [subject, setSubject] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastRef = useRef<Toast>(null);

  // Reset state every time the dialog opens
  useEffect(() => {
    if (visible) {
      setSubject(null);
      setMessage("");
      setAttachments([]);
      setSending(false);
    }
  }, [visible]);

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setAttachments((prev) => [...prev, ...newFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  /** Escape HTML special chars and convert newlines to <br/>. */
  const messageToHtml = (text: string): string => {
    const escaped = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
    return `<p>${escaped.replace(/\r?\n/g, "<br/>")}</p>`;
  };

  const handleSend = async () => {
    if (!subject || !message.trim()) {
      return;
    }

    try {
      setSending(true);

      const formData = new FormData();
      formData.append(
        "MpiId",
        providerInfo?.mpi != null ? String(providerInfo.mpi) : ""
      );
      formData.append("ToList", providerInfo?.primaryContactEmail || "");
      formData.append("CcList", "");
      formData.append("BccList", "");
      formData.append("Subject", subject);
      formData.append("Body", messageToHtml(message));
      formData.append("IsHtml", "true");

      attachments.forEach((file) => {
        // Field name must match the IFormFile (or IFormFileCollection)
        // property name on the API model.
        formData.append("Attachments", file, file.name);
      });

      const response = await httpService.post(
        ApiEndpoints.SendGeneralEmail,
        formData
      );

      if (response?.status >= 200 && response?.status < 300) {
        toastRef.current?.show({
          severity: "success",
          summary: "Email Sent",
          detail: "The email has been sent successfully.",
          life: 3000,
        });

        if (onSend) {
          onSend({
            subject: subject || "",
            message,
            attachments,
            toEmail: providerInfo?.primaryContactEmail,
          });
        }
        onHide();
      } else {
        throw new Error(`Unexpected response status: ${response?.status}`);
      }
    } catch (err) {
      console.error("Failed to send email:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Send Failed",
        detail: "Unable to send the email. Please try again.",
        life: 4000,
      });
    } finally {
      setSending(false);
    }
  };

  const contactName =
    `${providerInfo?.primaryContactFirstName || ""} ${
      providerInfo?.primaryContactLastName || ""
    }`.trim() || "-";

  const footer = (
    <div className="send-email-footer">
      <Button
        label="Cancel"
        className="p-button-outlined"
        onClick={onHide}
        disabled={sending}
      />
      <Button
        label={sending ? "Sending..." : "Send"}
        icon={sending ? "pi pi-spin pi-spinner" : undefined}
        onClick={handleSend}
        disabled={!subject || !message?.trim() || sending}
      />
    </div>
  );

  return (
    <>
      <Toast
        ref={toastRef}
        appendTo={typeof document !== "undefined" ? document.body : undefined}
        position="top-right"
        style={{ zIndex: 100000 }}
      />
      <Dialog
        header="Send Email to Primary Contact"
        visible={visible}
        onHide={onHide}
        className="send-email-dialog"
        modal
        draggable={false}
        resizable={false}
        dismissableMask
        breakpoints={{ "1400px": "60vw", "1199px": "70vw", "991px": "80vw", "767px": "92vw" }}
        style={{ width: "730px", maxWidth: "95vw" }}
        contentStyle={{ maxHeight: "75vh", overflowY: "auto" }}
        footer={footer}
      >
      <div className="send-email-body">
        <div className="send-email-section-title">Provider Details</div>
        <div className="send-email-provider-table-wrapper">
          <table className="send-email-provider-table">
            <thead>
              <tr>
                <th>Agency Name</th>
                <th>MPI</th>
                <th>Primary Contact Name</th>
                <th>Primary Contact Email</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{providerInfo?.agencyName || "-"}</td>
                <td>{providerInfo?.mpi || "-"}</td>
                <td>{contactName}</td>
                <td>
                  {providerInfo?.primaryContactEmail ? (
                    <a href={`mailto:${providerInfo.primaryContactEmail}`}>
                      {providerInfo.primaryContactEmail}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="send-email-field">
          <label className="send-email-label">
            Subject: <span className="send-email-required">*</span>
          </label>
          <Dropdown
            value={subject}
            options={subjectOptions}
            onChange={(e: DropdownChangeEvent) => setSubject(e.value)}
            placeholder="Select"
            className="send-email-dropdown"
          />
        </div>

        <div className="send-email-field">
          <label className="send-email-label">
            Message: <span className="send-email-required">*</span>
          </label>
          <InputTextarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Draft your message here."
            rows={6}
            className="send-email-textarea"
            autoResize={false}
          />
        </div>

        <div className="send-email-attachments">
          <div className="send-email-attachments-row">
            <label className="send-email-label">Attachment(s):</label>
            <Button
              label="Upload"
              onClick={() => fileInputRef.current?.click()}
              className="send-email-upload-btn"
            />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
              onChange={handleAttachmentSelect}
            />
          </div>
          <div className="send-email-attachment-list">
            {attachments.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="send-email-attachment-item"
              >
                <a href="#" onClick={(e) => e.preventDefault()}>
                  {file.name}
                </a>
                <i
                  className="pi pi-trash send-email-attachment-remove"
                  onClick={() => handleRemoveAttachment(index)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      </Dialog>
    </>
  );
};

export default SendEmailDialogComponent;
